import React, { useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Button } from 'devextreme-react/button';
import { Popup } from 'devextreme-react/popup';
import { useAppDispatch } from './contexts/hooks';
import useApi from './components/library/login-form/LoginForm.api';
import { newToast } from './components';

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, resetErrorBoundary }) => {
  const [showDetails, setShowDetails] = useState(false);

  const handleReload = () => {
    window.location.reload();
  };

  const handleReset = () => {
    resetErrorBoundary();
  };

  const copyErrorToClipboard = () => {
    const errorText = `Error: ${error.message}\n\nStack Trace:\n${error.stack}`;
    navigator.clipboard
      .writeText(errorText)
      .then(() => {
        newToast('SUCCESS', 'Error copiado al portapapeles', 3);
      })
      .catch(() => {
        newToast('ERROR', 'No se pudo copiar el error', 3);
      });
  };

  return (
    <Popup
      title='Error de la Aplicación'
      visible={true}
      onHiding={() => {}} // No permitir cerrar
      showCloseButton={false}
      width={'90%'}
      maxWidth={500}
      height='auto'
      dragEnabled={false}
      resizeEnabled={false}
      container='body'
      shading={true}
      shadingColor='rgba(0, 0, 0, 0.8)'
      wrapperAttr={{ style: { zIndex: 100000 } }}
      position={{ my: 'center', at: 'center', of: window }}>
      <div style={{ padding: '20px 0' }}>
        {/* Mensaje principal */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '15px' }}>⚠️</div>
          <h3 style={{ margin: '0 0 10px 0', color: '#dc3545', fontSize: '20px' }}>Ha ocurrido un error inesperado</h3>
          <p style={{ margin: 0, color: '#6c757d', fontSize: '14px', lineHeight: '1.4' }}>
            La aplicación encontró un problema y necesita reiniciarse.
          </p>
        </div>

        {/* Error message */}
        <div
          style={{
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '6px',
            padding: '12px',
            marginBottom: '20px',
            fontSize: '13px',
          }}>
          <strong style={{ color: '#dc3545' }}>Error:</strong>
          <div style={{ marginTop: '5px', color: '#495057' }}>{error.message}</div>
        </div>

        {/* Botones principales */}
        <div
          style={{
            display: 'flex',
            gap: '10px',
            justifyContent: 'center',
            marginBottom: '15px',
            flexWrap: 'wrap',
          }}>
          <Button 
            text='Recargar Aplicación' 
            type='default' 
            stylingMode='contained' 
            onClick={handleReload} 
            icon='refresh'
            width={window.innerWidth < 768 ? '100%' : 'auto'}
          />
          <Button 
            text='Intentar Continuar' 
            stylingMode='outlined' 
            onClick={handleReset} 
            icon='check'
            width={window.innerWidth < 768 ? '100%' : 'auto'}
          />
        </div>

        {/* Botón de detalles */}
        <div style={{ textAlign: 'center' }}>
          <Button 
            text={showDetails ? 'Ocultar detalles' : 'Ver detalles técnicos'} 
            stylingMode='text' 
            onClick={() => setShowDetails(!showDetails)} 
            icon={showDetails ? 'chevronup' : 'chevrondown'}
            width={window.innerWidth < 768 ? '100%' : 'auto'}
          />
          {showDetails && (
            <Button 
              text='Copiar error' 
              stylingMode='text' 
              onClick={copyErrorToClipboard} 
              icon='copy' 
              style={{ marginLeft: window.innerWidth < 768 ? '0' : '10px', marginTop: window.innerWidth < 768 ? '10px' : '0' }}
              width={window.innerWidth < 768 ? '100%' : 'auto'}
            />
          )}
        </div>

        {/* Detalles técnicos */}
        {showDetails && (
          <div
            style={{
              marginTop: '15px',
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '6px',
              padding: '12px',
              fontSize: '12px',
              fontFamily: 'monospace',
            }}>
            <div style={{ marginBottom: '10px' }}>
              <strong>Stack Trace:</strong>
            </div>
            <div
              style={{
                maxHeight: '200px',
                overflow: 'auto',
                backgroundColor: '#ffffff',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                padding: '8px',
                whiteSpace: 'pre-wrap',
                fontSize: '11px',
              }}>
              {error.stack}
            </div>

            <div style={{ marginTop: '10px', fontSize: '11px', color: '#6c757d' }}>
              <div>
                <strong>Timestamp:</strong> {new Date().toLocaleString()}
              </div>
              <div>
                <strong>URL:</strong> {window.location.href}
              </div>
            </div>
          </div>
        )}
      </div>
    </Popup>
  );
};

export const AppErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { logOut } = useApi();

  const handleError = (error: Error, info: { componentStack: string }) => {
    console.error('❌ Error atrapado en el ErrorBoundary:', error, info);

    // Log adicional para debugging
    console.group('🔍 Error Boundary Details');
    console.log('Error Name:', error.name);
    console.log('Error Message:', error.message);
    console.log('Error Stack:', error.stack);
    console.log('Component Stack:', info.componentStack);
    console.log('Timestamp:', new Date().toISOString());
    console.log('URL:', window.location.href);
    console.groupEnd();

    // Limpia la store
    dispatch(logOut);

    // Toast de notificación
    newToast('ERROR', 'Error crítico detectado - La aplicación se ha reiniciado', 10);
  };

  const handleReset = () => {
    // Limpiar cualquier estado persistente si es necesario
    console.log('🔄 Error Boundary Reset - Intentando recuperar aplicación');
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleError} onReset={handleReset}>
      {children}
    </ErrorBoundary>
  );
};
